import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PostCalendarGrid } from "./PostCalendarGrid";
import { PostQueue } from "./PostQueue";

export function CalendarQueuePanel() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth()); // 0-indexed
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterState, setFilterState] = useState('all');

  const handlePrevMonth = () => {
    if (month === 0) { setYear(y => y - 1); setMonth(11); }
    else setMonth(m => m - 1);
  };
  const handleNextMonth = () => {
    if (month === 11) { setYear(y => y + 1); setMonth(0); }
    else setMonth(m => m + 1);
  };

  // Fetch posts for the current month (for calendar dots)
  const startOfMonth = `${year}-${String(month + 1).padStart(2, '0')}-01T00:00:00.000Z`;
  const startOfNext = month === 11
    ? `${year + 1}-01-01T00:00:00.000Z`
    : `${year}-${String(month + 2).padStart(2, '0')}-01T00:00:00.000Z`;

  const { data: monthPosts = [] } = useQuery({
    queryKey: ['social_posts_month', year, month],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('social_posts' as any)
        .select('id, platform, status, scheduled_at')
        .gte('scheduled_at', startOfMonth)
        .lt('scheduled_at', startOfNext);
      if (error) throw error;
      return data ?? [];
    },
  });

  // Fetch posts for queue (all or filtered by selected date)
  const { data: queuePosts = [], isLoading: queueLoading } = useQuery({
    queryKey: ['social_posts', selectedDate, filterStatus, filterPlatform, filterState],
    queryFn: async () => {
      let q = supabase
        .from('social_posts' as any)
        .select('*')
        .order('scheduled_at', { ascending: true });

      if (selectedDate) {
        const dayStart = `${selectedDate}T00:00:00.000Z`;
        const dayEnd = `${selectedDate}T23:59:59.999Z`;
        q = q.gte('scheduled_at', dayStart).lte('scheduled_at', dayEnd);
      }
      if (filterStatus !== 'all') q = q.eq('status', filterStatus);
      if (filterPlatform !== 'all') q = q.eq('platform', filterPlatform);
      if (filterState !== 'all') q = q.eq('state_code', filterState);

      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
  });

  // Fetch campaigns and accounts for PostDialog
  const { data: campaigns = [] } = useQuery({
    queryKey: ['social_campaigns_list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('social_campaigns' as any)
        .select('id, name')
        .eq('status', 'active')
        .order('name');
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: accounts = [] } = useQuery({
    queryKey: ['social_accounts_list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('social_accounts' as any)
        .select('id, platform, username, handle, state_code')
        .eq('status', 'active')
        .order('platform')
        .order('state_code');
      if (error) throw error;
      return data ?? [];
    },
  });

  return (
    <div className="space-y-4">
      {/* Calendar */}
      <PostCalendarGrid
        year={year}
        month={month}
        posts={monthPosts as any}
        selectedDate={selectedDate}
        onSelectDate={setSelectedDate}
        onPrevMonth={handlePrevMonth}
        onNextMonth={handleNextMonth}
      />

      {/* Post Queue */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {selectedDate
              ? `Posts for ${new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}`
              : 'All Posts'
            }
          </CardTitle>
        </CardHeader>
        <CardContent>
          <PostQueue
            posts={queuePosts as any}
            isLoading={queueLoading}
            filterStatus={filterStatus}
            filterPlatform={filterPlatform}
            filterState={filterState}
            onFilterStatusChange={setFilterStatus}
            onFilterPlatformChange={setFilterPlatform}
            onFilterStateChange={setFilterState}
            campaigns={campaigns as any}
            accounts={accounts as any}
          />
        </CardContent>
      </Card>
    </div>
  );
}
